import ButtonNavLink from "@/lib/components/interactions/buttons/ButtonNavLink";
import { limitDrawerMenuDepth } from "@/lib/helpers/menuDepth.helpers";
import {
  DrawerMenuItem,
  DrawerMenuPropsType,
  useClickOutside,
} from "@/lib/main";
import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import NavbarSearchBar from "../navbar/NavbarSearchBar";

export default function DrawerMenu({
  drawerMenuItems,

  isDrawerOpen,
  isHashLinks,
  setIsDrawerOpen,

  addLoginButton,
  loginButtonColor,
  loginButtonLabel,
  loginButtonPath,

  addRegisterButton,
  registerButtonColor,
  registerButtonLabel,
  registerButtonPath,

  shoppingCartCount,

  addSearchBar,
  onSearchSubmit,
  searchBarPlaceholder,
}: DrawerMenuPropsType) {
  const safeDrawerMenuItems = useMemo(
    () => limitDrawerMenuDepth(drawerMenuItems),
    [drawerMenuItems],
  );

  const ulRef = useRef<HTMLUListElement>(null);
  const handleClick = () => setIsDrawerOpen(false);

  const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null);

  useEffect(() => {
    if (!isDrawerOpen) {
      const frame = window.requestAnimationFrame(() => {
        setOpenSubmenuId(null);
      });
      return () => window.cancelAnimationFrame(frame);
    }
  }, [isDrawerOpen]);

  // * close drawer if user clicks outside the menu
  const handleClickOutside = useClickOutside(ulRef, () => {
    setIsDrawerOpen(false);
  });

  const drawerContainerStyle: CSSProperties = {
    position: "relative",
    zIndex: "var(--z-index-header-drawer)",

    backgroundColor: "var(--color-background-drawer)",

    height: "calc(100dvh - var(--navbar-height))",
    overflowY: "auto",
    opacity: isDrawerOpen ? 1 : 0,
    pointerEvents: isDrawerOpen ? "auto" : "none",
    transform: isDrawerOpen ? "translateY(0)" : "translateY(-100dvh)",
    transition: isDrawerOpen
      ? "transform 0.5s ease-in-out, opacity 0.5s ease-in-out, visibility 0s"
      : "transform 0.5s ease-in-out, opacity 0.5s ease-in-out, visibility 0s linear 0.5s",
    visibility: isDrawerOpen ? "visible" : "hidden",
  };

  const searchBarContainerStyle: CSSProperties = {
    padding: "var(--spacing-md) var(--page-padding-horizontal)",
    borderBottom: "var(--border-width-xs) solid var(--color-border-subtle)",
  };

  const ulStyles: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    borderTop: "var(--border-width-xs) solid var(--color-border-subtle)",

    paddingBottom: "var(--spacing-lg)",

    listStyle: "none",
  };

  const ButtonLinkContainerStyle: CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    gap: "var(--spacing-3xl)",
    justifyContent: "center",
    paddingBottom: "var(--spacing-xl)",
  };

  return (
    <div style={drawerContainerStyle} onClick={handleClickOutside}>
      {addSearchBar ? (
        <div
          style={searchBarContainerStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <NavbarSearchBar
            fullWidth
            onSearchSubmit={onSearchSubmit}
            searchBarPlaceholder={searchBarPlaceholder}
          />
        </div>
      ) : null}

      <ul
        ref={ulRef}
        style={ulStyles}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {safeDrawerMenuItems.map((item) => (
          <DrawerMenuItem
            key={item.id}
            drawerMenuItem={item}
            isDrawerOpen={isDrawerOpen}
            isHashLinks={isHashLinks}
            setIsDrawerOpen={setIsDrawerOpen}
            shoppingCartCount={shoppingCartCount}
            openSubmenuId={openSubmenuId}
            setOpenSubmenuId={setOpenSubmenuId}
          />
        ))}
      </ul>

      {addLoginButton || addRegisterButton ? (
        <div style={ButtonLinkContainerStyle}>
          {addRegisterButton ? (
            <ButtonNavLink
              color={registerButtonColor || "brand"}
              to={registerButtonPath || "/register"}
              size="sm"
              onClick={handleClick}
            >
              {registerButtonLabel || "Register"}
            </ButtonNavLink>
          ) : null}

          {addLoginButton ? (
            <ButtonNavLink
              color={loginButtonColor || "outlined"}
              to={loginButtonPath || "/login"}
              size="sm"
              onClick={handleClick}
            >
              {loginButtonLabel || "Login"}
            </ButtonNavLink>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
